import React, { Component } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Container } from 'react-bootstrap';
import IconBreadcrumbs from "../custom-breadcrumb";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  LinearProgress,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ClipLoader } from 'react-spinners';

class Downloads extends Component {
  state = {
    isDownloading: false,
    downloadProgress: 0,
    error: null,
  };

  handleDownload = async () => {
    this.setState({ isDownloading: true, downloadProgress: 0, error: null });
    try {
      const response = await axios.get(
        'https://darknovaconquer.site/download/darknova%202025.rar',
        {
          responseType: 'blob',
          onDownloadProgress: (e) => {
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);
              this.setState({ downloadProgress: percent });
            }
          },
        }
      );

      saveAs(response.data, 'DarkNova 2025.rar');
    } catch (err) {
      console.error("Download failed", err);
      this.setState({ error: 'Download failed – please try again.' });
    } finally {
      setTimeout(() => {
        this.setState({ isDownloading: false, downloadProgress: 0 });
      }, 1000);
    }
  };

  render() {
    const { isDownloading, downloadProgress, error } = this.state;

    return (
      <Box sx={{ backgroundColor: '#111', py: 6, minHeight: '100vh' }}>
        <IconBreadcrumbs name="Download the game" />

        <Container sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/assets/installer-preview.png"
            alt="Installer Preview"
            sx={{ maxWidth: '100%', borderRadius: 2 }}
          />
        </Container>

        <Container>
          <Typography variant="h4" align="center" sx={{ color: 'white', fontWeight: 'bold', mb: 4 }}>
            Download Now and Begin Your Adventure
          </Typography>

          <Paper elevation={3} sx={{ p: 4, backgroundColor: '#1e1e1e', borderRadius: 4, color: '#ccc' }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose your preferred download option:
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleDownload}
                    fullWidth
                    startIcon={<CloudDownloadIcon fontSize="large" />}
                    sx={{
                      py: 2,
                      fontSize: '1.2rem',
                      borderRadius: 4,
                      '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading...' : 'Direct Download'}
                  </Button>

                  {isDownloading && (
                    <>
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}>
                        <ClipLoader color="#fff" size={48} />
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={downloadProgress}
                        sx={{
                          height: 20,
                          borderRadius: 5,
                          width: '100%',
                          backgroundColor: '#333',
                          mt: 1,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#4caf50',
                          },
                        }}
                      />
                    </>
                  )}

                  {error && (
                    <Typography color="error" variant="body2">
                      {error}
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    color="success"
                    href="https://u.pcloud.link/publink/show?code=XZVV0W5Zi3IfMVjKqSbyEQsiEXosokuHRwxV"
                    target="_blank"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    PCloud Mirror
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    href="https://mega.nz/file/iFknBDYD#5X9EvMrgBq3PxBGwRx7ek_QLqFOxn01TEoevKyQBQRY"
                    target="_blank"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    MEGA
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    href="https://www.mediafire.com/file/20vxzrb12x4u7lt/DarkNova_2025.rar/file"
                    target="_blank"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    MediaFire
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    href="https://drive.google.com/file/d/1BJRU54QJ2eiE8tz7wOaggdysLMDR6fmi/view?usp=sharing"
                    target="_blank"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    GoogleDrive
                  </Button>

                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
                  System Requirements
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table className="table table-dark table-bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{ textAlign: 'center' }}>Minimum</th>
                        <th style={{ textAlign: 'center' }}>Recommended</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>OS</td>
                        <td className="text-center">Windows XP or higher</td>
                        <td className="text-center">Windows 10 (64‑bit)</td>
                      </tr>
                      <tr>
                        <td>CPU</td>
                        <td className="text-center">Pentium III 800 MHz</td>
                        <td className="text-center">Intel Core i5 / AMD Ryzen 3</td>
                      </tr>
                      <tr>
                        <td>RAM</td>
                        <td className="text-center">128 MB</td>
                        <td className="text-center">4 GB</td>
                      </tr>
                      <tr>
                        <td>GPU</td>
                        <td className="text-center">16 MB DirectX-capable</td>
                        <td className="text-center">1 GB VRAM, DirectX 11</td>
                      </tr>
                      <tr>
                        <td>HDD</td>
                        <td className="text-center" colSpan={2}>1 GB free space</td>
                      </tr>
                      <tr>
                        <td>Sound</td>
                        <td className="text-center">DirectSound-capable</td>
                        <td className="text-center">DirectX 9.0c compatible</td>
                      </tr>
                      <tr>
                        <td>Additional</td>
                        <td className="text-center">Internet connection for updates</td>
                        <td className="text-center">Broadband recommended</td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    );
  }
}

export default Downloads;
